import React from 'react';
import { ApolloError } from '@apollo/client';

import { messageFromError } from '@oysterjs/core/graphql/errors';

import {
  CustomPresetState,
  type PresetGenerationProgressFieldsFragment,
  type PresetGenerationStep,
  type StartPresetGenerationInput,
  useGetPresetGenerationProgressQuery,
  useStartPresetGenerationMutation
} from '~/api/graphql';
import {
  type PresetGenerationStatus,
  derivePresetGenerationStatus
} from '~/components/workspaces/lib/convert-to-preset-wizard.utils';

const GENERIC_START_ERROR = 'Could not start preset generation. Please try again.';

/** A run makes three step transitions over ~35s, so a step lasts ~12s. */
const POLL_INTERVAL_MS = 5000;

export type PresetGeneration = {
  status: PresetGenerationStatus;
  progressStep: PresetGenerationStep | null;
  presetId: string | null;
  error: string | null;
  /** Re-runs generation with the same input (failure recovery). */
  retry: () => void;
  canRetry: boolean;
};

/**
 * Drives one preset generation. `startPresetGeneration` returns the preset it
 * created in state GENERATING; this then polls that preset until it reaches a
 * terminal state. Retry re-sends the same input, which mints a new preset.
 */
export function usePresetGeneration(input: StartPresetGenerationInput | null): PresetGeneration {
  const [startPresetGeneration] = useStartPresetGenerationMutation();
  const [preset, setPreset] = React.useState<PresetGenerationProgressFieldsFragment | null>(null);
  const [starting, setStarting] = React.useState(false);
  const [startError, setStartError] = React.useState<string | null>(null);
  const startedRef = React.useRef(false);

  const generating = preset?.state === CustomPresetState.Generating;

  // Held in state rather than read from `data`: `skip` flipping true on the
  // terminal state clears the hook's result, which would bounce the UI back to
  // the last GENERATING snapshot.
  const { data } = useGetPresetGenerationProgressQuery({
    variables: { id: preset?.id ?? '' },
    skip: !generating,
    pollInterval: POLL_INTERVAL_MS,
    // Nothing outside this hook reads the polled value, so skip the cache write
    // and the broadcast it triggers on every mounted watch query.
    fetchPolicy: 'no-cache'
  });

  React.useEffect(() => {
    // A poll that comes back empty is not a failed generation — the run
    // continues server-side, so keep the last known state and sample again.
    if (data?.customPreset) {
      setPreset(data.customPreset);
    }
  }, [data]);

  const start = React.useCallback(async () => {
    if (!input) return;
    setStarting(true);
    setStartError(null);
    setPreset(null);

    try {
      const res = await startPresetGeneration({ variables: { input } });
      const created = res.data?.startPresetGeneration;
      if (!created) {
        // errorPolicy 'all' lands server errors here rather than rejecting, and
        // res.errors[0].message carries SQL and bound args — never relay it raw.
        setStartError(
          messageFromError(new ApolloError({ graphQLErrors: res.errors }), GENERIC_START_ERROR)
        );
        return;
      }
      setPreset(created);
    } catch (error) {
      setStartError(messageFromError(error, GENERIC_START_ERROR));
    } finally {
      setStarting(false);
    }
  }, [input, startPresetGeneration]);

  React.useEffect(() => {
    if (input && !startedRef.current) {
      startedRef.current = true;
      void start();
    }
  }, [input, start]);

  const retry = React.useCallback(() => void start(), [start]);

  const status = derivePresetGenerationStatus({
    starting,
    startFailed: startError != null,
    presetState: preset?.state
  });

  return {
    status,
    progressStep: preset?.generation?.step ?? null,
    // The preset exists from the moment generation starts, so it is withheld
    // until terminal rather than resolved at the end.
    presetId: status === 'completed' ? (preset?.id ?? null) : null,
    // generation.error is the single fixed, user-safe message the workflow
    // persists (workflows.PresetGenerationFailureMessage) — rendered, not
    // replaced by a second copy of the same sentence on this side.
    error: startError ?? preset?.generation?.error ?? null,
    retry,
    canRetry: input != null
  };
}
